/**
 * AIVideoInterviewSection
 *
 * Displays the VA's publicly-shared AI Video Interview on their public profile.
 * Each recorded round can be watched independently via round-selector tabs.
 * Falls back to a single video for legacy sessions (only one video_url).
 *
 * Data: ai_interview_sessions where is_public = true AND status = 'completed'
 * Access: RLS policy "Public completed interviews are visible" allows unauthenticated reads.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Play, Pause, Volume2, VolumeX, ChevronDown, ChevronUp,
  MessageSquare, Award, Clock, CheckCircle
} from 'lucide-react';

function formatTime(secs) {
  if (!secs || !isFinite(secs) || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Only allow http(s) URLs to prevent javascript:/data: injection */
function isSafeUrl(url) {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

const AIVideoInterviewSection = ({ interview, vaName = '', isPublicProfile = false }) => {
  const videoRef   = useRef(null);
  const sectionRef = useRef(null);

  const [isPlaying,      setIsPlaying]      = useState(false);
  const [isMuted,        setIsMuted]        = useState(false);
  const [currentTime,    setCurrentTime]    = useState(0);
  const [duration,       setDuration]       = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [videoError,     setVideoError]     = useState(false);

  // Build per-round Q&A pairs (memoized to avoid recomputation on timeupdate renders)
  const qaRounds = useMemo(() => {
    const log = interview?.conversation_log || [];
    return log.reduce((rounds, entry, idx) => {
      if (entry.type !== 'ai') return rounds;
      const next = log[idx + 1];
      rounds.push({
        round:     entry.round || rounds.length + 1,
        question:  entry.content,
        answer:    next?.type === 'user' ? next.content : null,
        focusArea: entry.focusArea || '',
        videoUrl:  next?.type === 'user'
          ? (next.videoChunkUrl || next.videoUrl || null)
          : null,
      });
      return rounds;
    }, []);
  }, [interview?.conversation_log]);

  const completedRounds = interview?.rounds_completed ?? qaRounds.length;
  const totalRounds     = interview?.total_rounds ?? 5;

  // activeRound defaults to first available round (not always 1)
  const [activeRound, setActiveRound] = useState(() =>
    qaRounds.length > 0 ? qaRounds[0].round : 1
  );

  // Active video URL — validated to prevent javascript:/data: injection
  const activeRoundData = useMemo(
    () => qaRounds.find(r => r.round === activeRound),
    [qaRounds, activeRound]
  );
  const activeVideoUrl = useMemo(() => {
    const raw =
      activeRoundData?.videoUrl ||
      (activeRound === completedRounds ? interview?.video_url : null);
    return isSafeUrl(raw) ? raw : null;
  }, [activeRoundData, activeRound, completedRounds, interview?.video_url]);

  // Show round tabs whenever there are multiple rounds
  const showRoundTabs = completedRounds > 1;

  // Reset player state when the active round or video URL changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setVideoError(false);
  }, [activeRound, activeVideoUrl]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDuration   = () => { if (isFinite(video.duration)) setDuration(video.duration); };
    const onPlay       = () => setIsPlaying(true);
    const onPause      = () => setIsPlaying(false);
    const onEnded      = () => setIsPlaying(false);

    video.addEventListener('timeupdate',     onTimeUpdate);
    video.addEventListener('loadedmetadata', onDuration);
    video.addEventListener('play',           onPlay);
    video.addEventListener('pause',          onPause);
    video.addEventListener('ended',          onEnded);

    return () => {
      video.removeEventListener('timeupdate',     onTimeUpdate);
      video.removeEventListener('loadedmetadata', onDuration);
      video.removeEventListener('play',           onPlay);
      video.removeEventListener('pause',          onPause);
      video.removeEventListener('ended',          onEnded);
    };
  }, [activeVideoUrl]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    isPlaying ? v.pause() : v.play();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration || !isFinite(duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * duration;
  };

  const handleSeekKeyDown = (e) => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration)) return;
    if (e.key === 'ArrowRight') v.currentTime = Math.min(v.currentTime + 5, v.duration);
    if (e.key === 'ArrowLeft')  v.currentTime = Math.max(v.currentTime - 5, 0);
  };

  // Nothing to render
  if (!interview) return null;

  return (
    <section ref={sectionRef} className="content-container py-10" id="ai-interview">
      <div className="mx-auto w-full lg:max-w-4xl">

        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
              <Award className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI Video Interview</h2>
              <p className="text-sm text-gray-500">
                Watch {vaName ? `${vaName}'s` : "this VA's"} recorded AI-guided interview
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
              <CheckCircle className="w-3.5 h-3.5" />
              {completedRounds}/{totalRounds} Rounds
            </span>
            {interview.completed_at && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                <Clock className="w-3.5 h-3.5" />
                {formatDate(interview.completed_at)}
              </span>
            )}
          </div>
        </div>

        {/* Round Selector Tabs */}
        {showRoundTabs && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Array.from({ length: completedRounds }, (_, i) => i + 1).map((n) => {
              const roundData = qaRounds.find(r => r.round === n);
              const hasVideo  = !!roundData?.videoUrl ||
                (n === completedRounds && !!interview?.video_url);
              return (
                <button
                  key={n}
                  onClick={() => hasVideo && setActiveRound(n)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeRound === n
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : hasVideo
                        ? 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-400 hover:text-emerald-700'
                        : 'bg-gray-100 border border-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!hasVideo}
                  title={hasVideo ? `Watch Round ${n}` : `Round ${n} — no video`}
                >
                  Round {n}
                </button>
              );
            })}
          </div>
        )}

        {/* Video Player Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {/* ── Video player with custom controls ── */}
          {activeVideoUrl && (
            <div className="relative bg-black">
              <video
                key={activeVideoUrl}
                ref={videoRef}
                src={activeVideoUrl}
                className="w-full max-h-[480px] object-contain"
                preload="metadata"
                playsInline
                onError={() => setVideoError(true)}
              />

              {videoError && (
                <div className="absolute inset-0 bg-gray-900 flex items-center justify-center flex-col gap-3 p-6">
                  <Play className="w-12 h-12 text-gray-400" />
                  <p className="text-gray-300 text-sm text-center">
                    Unable to load video. The recording may have expired or been removed.
                  </p>
                </div>
              )}

              {/* Custom Controls Bar */}
              {!videoError && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                  <div
                    className="w-full h-1.5 bg-white/30 rounded-full mb-3 cursor-pointer"
                    role="slider"
                    aria-label="Video progress"
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration) || 100}
                    tabIndex={0}
                    onClick={handleSeek}
                    onKeyDown={handleSeekKeyDown}
                  >
                    <div
                      className="h-full bg-emerald-400 rounded-full transition-all"
                      style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={togglePlay}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                      >
                        {isPlaying
                          ? <Pause className="w-4 h-4 text-white" />
                          : <Play  className="w-4 h-4 text-white ml-0.5" />
                        }
                      </button>
                      <button
                        onClick={toggleMute}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                      >
                        {isMuted
                          ? <VolumeX className="w-4 h-4 text-white" />
                          : <Volume2 className="w-4 h-4 text-white" />
                        }
                      </button>
                      <span className="text-white text-xs font-mono">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                    {showRoundTabs && (
                      <span className="text-white/70 text-xs font-medium">
                        Round {activeRound} of {completedRounds}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── No video available for this round ── */}
          {!activeVideoUrl && (
            <div className="py-16 flex flex-col items-center gap-3 text-gray-400">
              <Play className="w-12 h-12" />
              <p className="text-sm">
                {showRoundTabs
                  ? `No video available for Round ${activeRound}.`
                  : 'Video recording not available.'
                }
              </p>
            </div>
          )}

          {/* Footer bar */}
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Award className="w-4 h-4 text-emerald-500" />
              <span className="font-medium">AI-Guided Interview</span>
              {interview.video_duration_secs > 0 && (
                <span className="text-gray-400">· {formatTime(interview.video_duration_secs)}</span>
              )}
            </div>

            {qaRounds.length > 0 && (
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                {showTranscript ? 'Hide' : 'View'} Q&amp;A
                {showTranscript
                  ? <ChevronUp   className="w-4 h-4" />
                  : <ChevronDown className="w-4 h-4" />
                }
              </button>
            )}
          </div>

          {/* Transcript / Q&A */}
          {showTranscript && qaRounds.length > 0 && (
            <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
              <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold pt-4">
                Interview Transcript
              </p>
              {qaRounds.map((r) => (
                <div
                  key={r.round}
                  className={`space-y-2 rounded-xl p-2 -mx-2 transition-colors ${
                    r.round === activeRound ? 'bg-emerald-50/60' : ''
                  }`}
                >
                  {/* Round label + watch button */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Round {r.round}
                    </span>
                    {r.videoUrl && showRoundTabs && (
                      <button
                        onClick={() => {
                          setActiveRound(r.round);
                          setShowTranscript(false);
                          sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" /> Watch this round
                      </button>
                    )}
                  </div>

                  {/* AI Question */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-emerald-700 text-xs font-bold">AI</span>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-xl rounded-tl-sm px-4 py-3">
                      {r.focusArea && (
                        <span className="inline-block text-xs font-semibold text-emerald-600 mb-1 uppercase tracking-wide">
                          {r.focusArea}
                        </span>
                      )}
                      <p className="text-sm text-gray-800">{r.question}</p>
                    </div>
                  </div>

                  {/* VA Answer */}
                  {r.answer && (
                    <div className="flex items-start gap-3 justify-end">
                      <div className="flex-1 bg-emerald-50 rounded-xl rounded-tr-sm px-4 py-3 ml-8">
                        <p className="text-sm text-gray-800">{r.answer}</p>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">VA</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Banner */}
        {isPublicProfile && (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-emerald-900 text-sm mb-1">About This AI Interview</h4>
                <p className="text-sm text-emerald-800">
                  This interview was conducted by an AI assistant and consists of {totalRounds} personalized rounds
                  based on the VA's profile and skills. It gives you a direct look at how{' '}
                  {vaName ? `${vaName}` : 'this VA'} communicates and approaches professional questions.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default AIVideoInterviewSection;
